import { addDays, format, isEqual, parseISO } from 'date-fns'
import * as Yup from 'yup'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import { clearEmptyFields } from '../../../utils/formHelpers'
import {
  CONTRACT_FORM_CONTRACT_ID_MAX_LENGTH,
  CONTRACT_FORM_FIELD_NAME_BUILDING,
  CONTRACT_FORM_FIELD_NAME_CONTRACT_ID,
  CONTRACT_FORM_FIELD_NAME_DURATION,
  CONTRACT_FORM_FIELD_NAME_END_DATE,
  CONTRACT_FORM_FIELD_NAME_FLAT,
  CONTRACT_FORM_FIELD_NAME_INHABITANT,
  CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER,
  CONTRACT_FORM_FIELD_NAME_START_DATE,
  CONTRACT_FORM_INITIAL_VALUES,
} from '../Contract.constants'

function toContractApiDate(date) {
  return date ? format(date, DATE_API_DATA_FORMAT_SHORT) : null
}

function toContractInhabitantSelectItem({ fullname, id }) {
  return {
    label: fullname,
    value: id,
  }
}

export function getContractFormInitialValues({
  contract,
  initialBuilding,
  initialFlat,
  initialUser,
} = EMPTY_OBJECT) {
  if (contract) {
    return {
      [CONTRACT_FORM_FIELD_NAME_BUILDING]: contract.building
        ? { label: contract.building.title, value: contract.building.id }
        : null,
      [CONTRACT_FORM_FIELD_NAME_CONTRACT_ID]:
        contract.contract_id ?? EMPTY_STRING,
      [CONTRACT_FORM_FIELD_NAME_DURATION]: contract.duration ?? EMPTY_STRING,
      [CONTRACT_FORM_FIELD_NAME_END_DATE]: contract.end_date
        ? parseISO(contract.end_date)
        : null,
      [CONTRACT_FORM_FIELD_NAME_FLAT]: contract.flat
        ? { label: contract.flat.number, value: contract.flat.id }
        : null,
      [CONTRACT_FORM_FIELD_NAME_INHABITANT]:
        initialUser ??
        (contract.inhabitant
          ? toContractInhabitantSelectItem(contract.inhabitant)
          : null),
      [CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER]:
        contract.purchase_contract_number ?? EMPTY_STRING,
      [CONTRACT_FORM_FIELD_NAME_START_DATE]: contract.start_date
        ? parseISO(contract.start_date)
        : null,
    }
  }

  return {
    ...CONTRACT_FORM_INITIAL_VALUES,
    [CONTRACT_FORM_FIELD_NAME_BUILDING]: initialBuilding ?? null,
    [CONTRACT_FORM_FIELD_NAME_FLAT]: initialFlat ?? null,
    [CONTRACT_FORM_FIELD_NAME_INHABITANT]: initialUser ?? null,
  }
}

export function getContractValidationSchema(t) {
  return Yup.object({
    [CONTRACT_FORM_FIELD_NAME_BUILDING]: Yup.object().nullable().required(true),
    [CONTRACT_FORM_FIELD_NAME_CONTRACT_ID]: Yup.string()
      .max(
        CONTRACT_FORM_CONTRACT_ID_MAX_LENGTH,
        t('Error.MaxLengthKey', {
          length: CONTRACT_FORM_CONTRACT_ID_MAX_LENGTH,
        })
      )
      .nullable(),
    [CONTRACT_FORM_FIELD_NAME_DURATION]: Yup.number().nullable(),
    [CONTRACT_FORM_FIELD_NAME_END_DATE]: Yup.date()
      .nullable()
      .min(
        Yup.ref(CONTRACT_FORM_FIELD_NAME_START_DATE),
        t('Contract.EndDateBeforeStartDateErrorKey')
      ),
    [CONTRACT_FORM_FIELD_NAME_FLAT]: Yup.object().nullable().required(true),
    [CONTRACT_FORM_FIELD_NAME_INHABITANT]: Yup.object()
      .nullable()
      .required(true),
    [CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER]: Yup.string()
      .max(
        CONTRACT_FORM_CONTRACT_ID_MAX_LENGTH,
        t('Error.MaxLengthKey', {
          length: CONTRACT_FORM_CONTRACT_ID_MAX_LENGTH,
        })
      )
      .nullable(),
    [CONTRACT_FORM_FIELD_NAME_START_DATE]: Yup.date().nullable(),
  })
}

export function getContractFormPayload(values) {
  return clearEmptyFields({
    [CONTRACT_FORM_FIELD_NAME_BUILDING]:
      values[CONTRACT_FORM_FIELD_NAME_BUILDING]?.value,
    [CONTRACT_FORM_FIELD_NAME_CONTRACT_ID]:
      values[CONTRACT_FORM_FIELD_NAME_CONTRACT_ID],
    [CONTRACT_FORM_FIELD_NAME_DURATION]:
      values[CONTRACT_FORM_FIELD_NAME_DURATION] || null,
    [CONTRACT_FORM_FIELD_NAME_END_DATE]: toContractApiDate(
      values[CONTRACT_FORM_FIELD_NAME_END_DATE]
    ),
    [CONTRACT_FORM_FIELD_NAME_FLAT]:
      values[CONTRACT_FORM_FIELD_NAME_FLAT]?.value,
    [CONTRACT_FORM_FIELD_NAME_INHABITANT]:
      values[CONTRACT_FORM_FIELD_NAME_INHABITANT]?.value,
    [CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER]:
      values[CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER],
    [CONTRACT_FORM_FIELD_NAME_START_DATE]: toContractApiDate(
      values[CONTRACT_FORM_FIELD_NAME_START_DATE]
    ),
  })
}

export function getContractUpdatePayload(values) {
  return {
    [CONTRACT_FORM_FIELD_NAME_CONTRACT_ID]:
      values[CONTRACT_FORM_FIELD_NAME_CONTRACT_ID] || null,
    [CONTRACT_FORM_FIELD_NAME_DURATION]:
      values[CONTRACT_FORM_FIELD_NAME_DURATION] || null,
    [CONTRACT_FORM_FIELD_NAME_END_DATE]: toContractApiDate(
      values[CONTRACT_FORM_FIELD_NAME_END_DATE]
    ),
    [CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER]:
      values[CONTRACT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER] || null,
    [CONTRACT_FORM_FIELD_NAME_START_DATE]: toContractApiDate(
      values[CONTRACT_FORM_FIELD_NAME_START_DATE]
    ),
  }
}

export function getContractDelayedDate({ endDate, evictionDelay }) {
  return endDate ? addDays(endDate, evictionDelay) : null
}

export function getContractDisplayedDelayedDate({
  delayedDate,
  endDate,
  evictionDelay,
  initialEndDate,
}) {
  const isEndDateUnchanged =
    endDate && initialEndDate
      ? isEqual(endDate, initialEndDate)
      : endDate === initialEndDate

  if (delayedDate && isEndDateUnchanged) {
    return parseISO(delayedDate)
  }

  return getContractDelayedDate({ endDate, evictionDelay })
}

export function getContractCreatedNotification(t) {
  return {
    text: t('Notification.ContractCreatedTextKey'),
    title: t('Notification.ContractCreatedTitleKey'),
  }
}

export function getContractCreationErrorNotification(t) {
  return {
    text: t('Notification.ContractCreationErrorTextKey'),
    title: t('Notification.ContractCreationErrorTitleKey'),
  }
}

export function getContractUpdatedNotification(t) {
  return {
    text: t('Notification.ContractUpdatedTextKey'),
    title: t('Notification.ContractUpdatedTitleKey'),
  }
}

export function getContractUpdateErrorNotification(t) {
  return {
    text: t('Notification.ContractUpdateErrorTextKey'),
    title: t('Notification.ContractUpdateErrorTitleKey'),
  }
}
